import { z } from 'zod'
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { reportError } from '@/lib/error-reporter'
import { supabase } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { PageSpinner } from '@/components/ui/spinner'
import { Logo } from '@/components/logo'

const searchSchema = z.object({
  token: z.string().catch(''),
})

const roleLabels: Record<string, string> = {
  org_admin: 'an administrator',
  member: 'a member',
}

interface InvitationPreview {
  org_id: string
  org_name: string
  inviter_name: string
  role: string
  already_accepted?: boolean
  unavailable?: string
}

function invitationPreviewQuery(token: string) {
  return queryOptions({
    queryKey: ['invitation-preview', token],
    queryFn: async (): Promise<InvitationPreview> => {
      if (!token.trim()) {
        throw new Error(
          'This invitation link is no longer valid. Ask the sender to invite you again.'
        )
      }

      const { data, error } = await supabase.functions.invoke(
        'verify-invitation',
        { body: { token, preview: true } }
      )

      if (data?.already_accepted) {
        return { already_accepted: true } as InvitationPreview
      }

      if (error || !data?.org_id) {
        return {
          unavailable:
            data?.error || 'This invitation link is no longer valid.',
        } as InvitationPreview
      }

      return data as InvitationPreview
    },
    retry: false,
  })
}

function InvitationComponent() {
  const navigate = useNavigate()
  const { token } = Route.useSearch()

  const {
    data: preview,
    isLoading,
    error,
  } = useQuery(invitationPreviewQuery(token))

  const {
    mutate: accept,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: async () => {
      const { data, error: fnError } = await supabase.functions.invoke(
        'verify-invitation',
        { body: { token } }
      )

      // Existing accounts sign in normally, then come back to accept.
      if (data?.requires_sign_in && data.org_id) {
        return { org_id: data.org_id as string, requiresSignIn: true }
      }

      if (fnError || !data?.token_hash || !data?.org_id) {
        throw new Error(
          data?.error ||
            'This invitation has already been accepted or is no longer available.'
        )
      }

      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: data.token_hash,
        type: 'email',
      })

      if (verifyError) {
        reportError(
          new Error(`Failed to verify invitation OTP: ${verifyError.message}`, {
            cause: verifyError,
          }),
          { toast: false }
        )
        throw new Error(
          'Failed to authenticate. Please try again or request a new invitation.'
        )
      }

      return { org_id: data.org_id as string, requiresSignIn: false }
    },
    onSuccess: ({ org_id, requiresSignIn }) => {
      if (requiresSignIn) {
        navigate({
          to: '/sign-in',
          search: { redirect: `/auth/accept-invite?org_id=${org_id}` },
          replace: true,
        })
        return
      }
      navigate({
        to: '/auth/accept-invite',
        search: { org_id },
        replace: true,
      })
    },
    onError: (error) => {
      toast.warning(error.message)
    },
  })

  if (isLoading) {
    return <PageSpinner message='Loading invitation...' />
  }

  return (
    <div className='container grid h-svh max-w-none items-center justify-center'>
      <div className='mx-auto flex w-full flex-col justify-center space-y-2 py-8 sm:w-[480px] sm:p-8'>
        <div className='mb-4 flex items-center justify-center'>
          <Logo className='mr-2 w-12' />
          <h1 className='text-xl font-medium'>Lumoo</h1>
        </div>

        {error || preview?.unavailable ? (
          <Card className='gap-4'>
            <CardHeader>
              <CardTitle className='text-lg tracking-tight'>
                Invitation Unavailable
              </CardTitle>
              <CardDescription>
                {error?.message ?? preview?.unavailable}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to='/sign-in' className='w-full'>
                <Button className='w-full'>Go to Sign In</Button>
              </Link>
            </CardContent>
          </Card>
        ) : preview?.already_accepted ? (
          <Card className='gap-4'>
            <CardHeader>
              <CardTitle className='text-lg tracking-tight'>
                Already Accepted
              </CardTitle>
              <CardDescription>
                This invitation has already been accepted. Sign in to access the
                organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to='/sign-in' className='w-full'>
                <Button className='w-full'>Go to Sign In</Button>
              </Link>
            </CardContent>
          </Card>
        ) : preview ? (
          <Card className='gap-4'>
            <CardHeader>
              <CardTitle className='text-lg tracking-tight'>
                You've Been Invited
              </CardTitle>
              <CardDescription>
                <strong>{preview.inviter_name}</strong> has invited you to join{' '}
                <strong>{preview.org_name}</strong> as{' '}
                {roleLabels[preview.role] ?? preview.role}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className='w-full'
                disabled={isPending || isSuccess}
                onClick={() => accept()}
              >
                {(isPending || isSuccess) && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                Accept Invitation
              </Button>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/auth/invitation')({
  component: InvitationComponent,
  validateSearch: searchSchema,
})
